import * as THREE from 'three/webgpu';
import { computeDirectionArrowWorldSizeFromPixels } from '../../manager/directionArrow/computeDirectionArrowWorldSizeFromPixels';
import { PointsMaterialFactory } from '../../material/PointsMaterialFactory';
import { MEASURE_ANCHOR_MARKER_RENDER_ORDER } from '../utils/measureOverlayRenderLayering';

/**
 * 测量锚点目标屏幕直径（px）。
 *
 * 说明：通过 `computeDirectionArrowWorldSizeFromPixels` 换算为世界 scale，
 * 在正交 zoom / 透视推拉时保持视口像素尺寸稳定。
 */
const MEASURE_ANCHOR_PIXEL_SIZE = 10;

/**
 * 测量拾取锚点标记。
 *
 * 设计说明：
 * 1. 使用 `THREE.Sprite` 固定在射线命中世界坐标，表达“用户点击位置”；
 * 2. 渲染前按相机与视口同步世界 scale，抵消正交 zoom 对精灵尺寸的影响；
 * 3. 颜色与测量选中绿色一致，便于与 MeasureTransitionState 联动识别；
 * 4. 标记不参与拾取，避免遮挡后续测量点击。
 */
export class MeasureAnchorMarker extends THREE.Sprite {
    /** 运行时类型标记。 */
    public readonly isMeasureAnchorMarker: boolean = true;

    /** 锚点世界坐标（供屏幕尺寸同步复用，避免 onBeforeRender 中重复分配）。 */
    private readonly worldPositionRef: THREE.Vector3 = new THREE.Vector3();

    /** 读取当前相机的回调。 */
    private cameraProvider: (() => THREE.Camera | null | undefined) | null = null;

    /** 读取当前视口容器的回调。 */
    private containerProvider: (() => HTMLElement | null | undefined) | null = null;

    /**
     * @param worldPosition 世界坐标拾取点。
     * @param nameSuffix 可选名称后缀，用于区分双选锚点槽位（如 `from` / `to`）。
     */
    public constructor(worldPosition: THREE.Vector3, nameSuffix?: string) {
        const material = PointsMaterialFactory.getMeasureAnchorSpriteMaterial().clone();
        material.depthWrite = false;
        material.depthTest = false;
        // 由 syncScreenScale 按像素换算世界 scale，需开启距离衰减才与换算公式一致。
        material.sizeAttenuation = true;
        super(material);
        this.worldPositionRef.copy(worldPosition);
        this.position.copy(worldPosition);
        this.name = nameSuffix
            ? `MeasureAnchorMarker-${nameSuffix}`
            : 'MeasureAnchorMarker';
        this.renderOrder = MEASURE_ANCHOR_MARKER_RENDER_ORDER;
        this.frustumCulled = false;
        this.userData.skipSelection = true;
        this.userData.isMeasureAnchorMarker = true;
        this.raycast = () => undefined;
        this.onBeforeRender = () => {
            this.syncScreenScale(this.cameraProvider?.(), this.containerProvider?.());
        };
    }

    /**
     * 绑定屏幕尺寸同步上下文。
     *
     * @param cameraProvider 当前相机读取回调。
     * @param containerProvider 当前视口容器读取回调。
     */
    public bindScreenSizeContext(
        cameraProvider: () => THREE.Camera | null | undefined,
        containerProvider: () => HTMLElement | null | undefined,
    ): void {
        this.cameraProvider = cameraProvider;
        this.containerProvider = containerProvider;
        this.syncScreenScale(cameraProvider(), containerProvider());
    }

    /**
     * 更新锚点世界坐标。
     *
     * @param worldPosition 新的世界坐标。
     */
    public setWorldPosition(worldPosition: THREE.Vector3): void {
        this.worldPositionRef.copy(worldPosition);
        this.position.copy(worldPosition);
        this.syncScreenScale(this.cameraProvider?.(), this.containerProvider?.());
    }

    /**
     * 按当前相机与视口同步锚点世界 scale，保持屏幕像素尺寸恒定。
     *
     * @param camera 当前相机。
     * @param container 当前视口容器。
     */
    public syncScreenScale(
        camera: THREE.Camera | null | undefined,
        container: HTMLElement | null | undefined,
    ): void {
        const worldScale = computeDirectionArrowWorldSizeFromPixels(
            camera,
            container,
            this.worldPositionRef,
            MEASURE_ANCHOR_PIXEL_SIZE,
        );
        this.scale.setScalar(worldScale);
    }

    /**
     * 释放锚点资源并从父级移除。
     */
    public dispose(): void {
        this.onBeforeRender = () => undefined;
        this.cameraProvider = null;
        this.containerProvider = null;
        this.material.dispose();
        if (this.parent) {
            this.parent.remove(this);
        }
    }
}
